import { Box, useTheme } from "@mui/material";
import AsteriskSolid from "../../../assets/faIcons/AsteriskSolid";
import CloseIcon from "@mui/icons-material/Close";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import IconButton from "@mui/material/IconButton";
import MuiDialogContent from "@mui/material/DialogContent";
import MuiDialogTitle from "@mui/material/DialogTitle";
import MuiSVGIcon from "../../IconPicker/MuiSVGIcon";
import Typography from "@mui/material/Typography";

function ModalDialog(props) {
  const {
    content,
    dialogActions,
    fullScreen = false,
    handleClose,
    hasFormFields,
    isTabs,
    maxWidth = "md",
    open,
    title,
    icon,
  } = props;

  const theme = useTheme();
  const { palette = {} } = theme;
  const { primary = {}, success = {}, background = {}, text = {}, divider, mode } = palette;
  const isDark = mode === "dark";

  // Brand colors
  const navyColor = primary?.main || "#002843";
  const greenColor = success?.main || "#A0C46D";
  const bgPaper = background?.paper || (isDark ? "#032d42" : "#ffffff");
  const bgDefault = background?.default || (isDark ? "#021e2d" : "#f8fafc");
  const textPrimary = text?.primary || (isDark ? "#ffffff" : "#1e293b");
  const textSecondary = text?.secondary || (isDark ? "#94a3b8" : "#64748b");
  const borderColor = divider || (isDark ? "rgba(148, 163, 184, 0.2)" : "#e2e8f0");
  const warningBg = isDark ? "rgba(245, 158, 11, 0.15)" : "linear-gradient(135deg, #fef3c7 0%, #fef9c3 100%)";
  const warningBorder = isDark ? "rgba(245, 158, 11, 0.4)" : "#fcd34d";
  const warningIconBg = isDark ? "rgba(245, 158, 11, 0.35)" : "#fbbf24";
  const warningText = isDark ? "#fcd34d" : "#92400e";

  const formFieldsExist =
    hasFormFields || (content && content.props && content.props.hasFormFields);

  return (
    <Dialog
      aria-labelledby={title}
      fullScreen={fullScreen}
      maxWidth={maxWidth}
      onClose={(e) => {
        e.preventDefault();
        handleClose();
      }}
      open={open}
      PaperProps={{
        sx: {
          borderRadius: "20px",
          boxShadow: isDark 
            ? "0 25px 60px -12px rgba(0, 0, 0, 0.5), 0 0 0 1px rgba(0, 0, 0, 0.2)"
            : "0 25px 60px -12px rgba(0, 0, 0, 0.3), 0 0 0 1px rgba(0, 0, 0, 0.05)",
          maxHeight: "90vh",
          minWidth: { xs: "90vw", sm: "500px", md: "600px" },
          maxWidth: "90vw",
          overflow: "hidden",
          backgroundColor: bgPaper,
          animation: "modalSlideIn 0.35s cubic-bezier(0.4, 0, 0.2, 1)",
          "@keyframes modalSlideIn": {
            from: { opacity: 0, transform: "scale(0.95) translateY(-20px)" },
            to: { opacity: 1, transform: "scale(1) translateY(0)" },
          },
        },
      }}
      BackdropProps={{
        sx: {
          backgroundColor: isDark ? "rgba(0, 0, 0, 0.7)" : "rgba(30, 41, 59, 0.6)",
          backdropFilter: "blur(8px)",
        },
      }}
    >
      {open ? (
        <>
          {!isTabs ? (
            <MuiDialogTitle
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "20px 28px",
                borderBottom: `1px solid ${borderColor}`,
                background: isDark 
                  ? `linear-gradient(180deg, ${bgPaper} 0%, ${bgDefault} 100%)`
                  : "linear-gradient(180deg, #ffffff 0%, #fafbfc 100%)",
                position: "relative",
                "&::after": {
                  content: '""',
                  position: "absolute",
                  bottom: 0,
                  left: 0,
                  right: 0,
                  height: "3px",
                  background: `linear-gradient(90deg, ${isDark ? greenColor : navyColor} 0%, ${greenColor} 100%)`,
                  opacity: 0.8,
                },
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: "14px" }}>
                {icon && (
                  <Box
                    sx={{
                      width: "42px",
                      height: "42px",
                      borderRadius: "12px",
                      background: isDark 
                        ? `linear-gradient(135deg, ${greenColor}25 0%, ${greenColor}10 100%)`
                        : `linear-gradient(135deg, ${greenColor}20 0%, ${navyColor}15 100%)`,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      border: `1px solid ${greenColor}${isDark ? '40' : '30'}`,
                    }}
                  >
                    {icon}
                  </Box>
                )}
                <Typography
                  variant="h6"
                  sx={{
                    color: isDark ? greenColor : navyColor,
                    fontFamily: '"Lato", sans-serif',
                    fontWeight: 700,
                    fontSize: "1.25rem",
                    letterSpacing: "-0.01em",
                  }}
                >
                  {title}
                </Typography>
              </Box>
              {handleClose ? (
                <IconButton
                  aria-label="close"
                  onClick={handleClose}
                  size="small"
                  sx={{
                    color: textSecondary,
                    backgroundColor: isDark ? "rgba(148, 163, 184, 0.1)" : "#ffffff",
                    border: `1px solid ${borderColor}`,
                    borderRadius: "10px",
                    padding: "8px",
                    transition: "all 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
                    "&:hover": {
                      backgroundColor: isDark ? "rgba(248, 113, 113, 0.15)" : "#fef2f2",
                      borderColor: isDark ? "rgba(248, 113, 113, 0.4)" : "#fecaca",
                      color: isDark ? "#f87171" : "#ef4444",
                      transform: "rotate(90deg)",
                    },
                  }}
                >
                  <CloseIcon sx={{ fontSize: "1.125rem" }} />
                </IconButton>
              ) : null}
            </MuiDialogTitle>
          ) : null}

          <MuiDialogContent
            sx={{
              padding: "28px",
              backgroundColor: bgPaper,
              overflow: "auto",
              // Custom scrollbar
              "&::-webkit-scrollbar": {
                width: "6px",
              },
              "&::-webkit-scrollbar-track": {
                background: "transparent",
              },
              "&::-webkit-scrollbar-thumb": {
                background: isDark ? "rgba(160, 196, 109, 0.3)" : "#cbd5e1",
                borderRadius: "3px",
                "&:hover": {
                  background: isDark ? "rgba(160, 196, 109, 0.5)" : "#94a3b8",
                },
              },
            }}
            dividers={false}
          >
            {formFieldsExist ? (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  padding: "14px 18px",
                  background: warningBg,
                  borderRadius: "12px",
                  marginBottom: "24px",
                  border: `1px solid ${warningBorder}`,
                  transition: "opacity 0.25s ease, transform 0.25s ease",
                  animation: "modalBannerIn 0.3s ease-out",
                  "@keyframes modalBannerIn": {
                    from: { opacity: 0, transform: "translateY(-8px)" },
                    to: { opacity: 1, transform: "translateY(0)" },
                  },
                }}
              >
                <Box
                  sx={{
                    width: "28px",
                    height: "28px",
                    borderRadius: "8px",
                    backgroundColor: warningIconBg,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  <MuiSVGIcon
                    icon={<AsteriskSolid />}
                    sx={{
                      color: "#ffffff",
                      fontSize: "10px",
                    }}
                  />
                </Box>
                <Typography
                  component="p"
                  sx={{
                    color: warningText,
                    fontWeight: 500,
                    fontSize: "0.875rem",
                  }}
                >
                  Required fields are marked with an asterisk
                </Typography>
              </Box>
            ) : null}

            {content}
          </MuiDialogContent>

          {dialogActions ? (
            <DialogActions
              sx={{
                padding: "18px 28px",
                borderTop: `1px solid ${borderColor}`,
                background: isDark 
                  ? `linear-gradient(180deg, ${bgDefault} 0%, rgba(0, 20, 31, 0.8) 100%)`
                  : "linear-gradient(180deg, #fafbfc 0%, #f1f5f9 100%)",
                gap: "12px",
              }}
            >
              {dialogActions}
            </DialogActions>
          ) : null}
        </>
      ) : null}
    </Dialog>
  );
}

export default ModalDialog;
